import * as React from 'react';

import { Check, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import moment from 'moment';

import Container from 'components/common/Container';
import TextInput from 'components/common/TextInput';
import api from 'utils/api';

import { useAddEventModal, type FormSensorType } from './utils';
import { useFetchEventTypes, type EventType } from '../tables/state';

const EVENTS_API = '/admin/events';

type DateTimePickerValue = Dayjs | null;
interface FormValues {
  buoyId: number;
  eventType?: EventType;
  comments: string;
}

// =============================================================================
export default function AddEventTableModal(props: any) {
  const [snackbar, setSnackbar] = React.useState('');
  const [startTime, setStartTime] = React.useState<DateTimePickerValue>(null);
  const [endTime, setEndTime] = React.useState<DateTimePickerValue>(null);
  const [selectedSensors, setSelectedSensors] = React.useState<
    Array<FormSensorType>
  >([]);
  const { eventTypes } = useFetchEventTypes();

  const formik = useFormik<FormValues>({
    initialValues: {
      comments: '',
      buoyId: 0
    },
    onSubmit: async (values) => {
      if (!values.buoyId || !values.eventType) {
        setSnackbar('Fields cannot be empty!');
        return;
      }
      if (!startTime || !endTime) {
        setSnackbar('Invalid start or end time');
        return;
      }
      if (startTime && endTime && endTime.diff(startTime) < 0) {
        setSnackbar('End time must be later than start time.');
        return;
      }
      const payload = convert(values);
      try {
        await api.post(EVENTS_API, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        props.handleRefreshTable();
        props.handleCloseAddModal();
      } catch (err: any) {
        console.log(err);
        setSnackbar('Server error, please try again later!');
      }
    }
  });

  const {
    buoys: locations,
    isGetBuoysPending: isLocationsPending,
    sensors
  } = useAddEventModal({
    setSnackbar,
    selectedBuoyId: formik.values.buoyId,
    setSelectedSensors,
    triggerUpdate: false
  });

  // =============================================================================
  // Styling
  const modalContainerStyle = {
    marginBottom: 48,
    marginTop: 48,
    border: '2px solid black',
    borderRadius: '20px',
    backgroundColor: 'white'
  };

  // =============================================================================
  return (
    <div
      style={{
        maxWidth: '100%',
        alignContent: 'center',
        border: 20,
        margin: 'auto'
      }}
    >
      <Container style={modalContainerStyle}>
        <div style={{ height: 40 }} />
        <Typography variant='h2' color='blue.900' align='center'>
          Add Event
        </Typography>
        <form
          onSubmit={formik.handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '40px 0'
          }}
        >
          <>
            <div
              style={{
                marginBottom: 32,
                width: '100%',
                maxWidth: 400
              }}
            >
              <DateTimePicker
                className='date-time-picker'
                renderInput={(props) => (
                  <TextField
                    {...props}
                    sx={{
                      borderRadius: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 4
                      }
                    }}
                  />
                )}
                label='Start Time'
                value={startTime}
                onChange={(newValue: DateTimePickerValue) => {
                  if (newValue === null || !newValue.isValid()) {
                    setStartTime(null);
                    return;
                  }
                  setStartTime(newValue);
                }}
              />
            </div>
            <div
              style={{
                marginBottom: 32,
                width: '100%',
                maxWidth: 400
              }}
            >
              <DateTimePicker
                className='date-time-picker'
                renderInput={(props) => (
                  <TextField
                    {...props}
                    sx={{
                      borderRadius: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 4
                      }
                    }}
                  />
                )}
                label='End Time'
                value={endTime}
                onChange={(newValue: DateTimePickerValue) => {
                  if (newValue === null || !newValue.isValid()) {
                    setEndTime(null);
                    return;
                  }
                  setEndTime(newValue);
                }}
              />
            </div>
            <div
              style={{
                width: '100%',
                maxWidth: 400,
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 6
              }}
            >
              <span
                style={{
                  fontWeight: 'bold'
                }}
              >
                Location
              </span>
              <Select
                displayEmpty
                variant='outlined'
                id='buoyId'
                name='buoyId'
                placeholder='Select a location'
                disabled={isLocationsPending}
                value={formik.values.buoyId}
                onChange={formik.handleChange}
                error={formik.touched.buoyId && Boolean(formik.errors.buoyId)}
                sx={{
                  borderRadius: 4,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 4
                  }
                }}
              >
                {locations.map(({ id, locationFF }) => (
                  <MenuItem key={id} value={id}>
                    {locationFF}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div
              style={{
                width: '100%',
                maxWidth: '400px',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '20px'
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold'
                  }}
                >
                  Sensors
                </span>

                <Chip
                  label='Select all'
                  disabled={
                    !formik.values.buoyId ||
                    selectedSensors.length === (sensors ?? []).length
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    if (sensors) {
                      setSelectedSensors(
                        sensors.map(
                          ({ ownerSensorClassId, ownerSensorId }) => ({
                            ownerSensorClassId,
                            ownerSensorId
                          })
                        )
                      );
                    }
                  }}
                />
                <Chip
                  label='Remove all'
                  icon={<DeleteOutline style={{ height: 16, width: 16 }} />}
                  style={{
                    paddingLeft: '3px'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSensors([]);
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}
              >
                {sensors?.map((sensor) => {
                  const isSelected =
                    selectedSensors.findIndex(
                      (v) =>
                        v.ownerSensorClassId === sensor.ownerSensorClassId &&
                        v.ownerSensorId === sensor.ownerSensorId
                    ) > -1;
                  return (
                    <Chip
                      icon={
                        isSelected ? (
                          <Check
                            style={{
                              height: '16px',
                              width: '16px'
                            }}
                          />
                        ) : undefined
                      }
                      variant={isSelected ? 'filled' : 'outlined'}
                      key={sensor.sensorClass}
                      label={sensor.sensorClass}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isSelected) {
                          setSelectedSensors((ssrs) =>
                            ssrs.filter(
                              (s) =>
                                s.ownerSensorClassId !==
                                  sensor.ownerSensorClassId &&
                                s.ownerSensorId !== sensor.ownerSensorId
                            )
                          );
                        } else {
                          setSelectedSensors((ssrs) => [
                            ...ssrs,
                            {
                              ownerSensorClassId: sensor.ownerSensorClassId,
                              ownerSensorId: sensor.ownerSensorId
                            }
                          ]);
                        }
                      }}
                    />
                  );
                })}
                {formik.values.buoyId && (!sensors || sensors.length === 0) && (
                  <div>
                    <span>No sensors are available for this location yet.</span>
                  </div>
                )}
              </div>
            </div>

            <Autocomplete
              options={eventTypes}
              value={formik.values.eventType}
              onChange={(_, newValue) =>
                formik.setFieldValue('eventType', newValue)
              }
              getOptionLabel={(option) => option.eventType}
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column'
              }}
              renderInput={(params) => {
                return (
                  <TextInput
                    textFieldProps={params}
                    variant='outlined'
                    id='event-type'
                    name='eventType'
                    label='Event type'
                    placeholder=''
                    error={
                      formik.touched.eventType &&
                      Boolean(formik.errors.eventType)
                    }
                    helperText={
                      formik.touched.eventType && formik.errors.eventType
                    }
                    labelStyle={{ marginLeft: 0, marginBottom: 6 }}
                    containerStyle={{
                      width: '100%',
                      maxWidth: 400,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    sx={{
                      borderRadius: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 4
                      }
                    }}
                  />
                );
              }}
            />

            <TextField
              variant='outlined'
              id='comments'
              name='comments'
              label='Additional Comments'
              multiline
              rows={6}
              placeholder='Optional'
              value={formik.values.comments}
              onChange={formik.handleChange}
              error={formik.touched.comments && Boolean(formik.errors.comments)}
              helperText={formik.touched.comments && formik.errors.comments}
              sx={{
                width: '100%',
                maxWidth: 400,
                borderRadius: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4
                }
              }}
            />
            <div style={{ height: 16 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type='button'
                variant='secondary'
                color='dark'
                onClick={handleClose}
                style={{ marginRight: 10 }}
              >
                Close
              </Button>
              <Button type='submit' variant='primary' color='dark'>
                Save Changes
              </Button>
            </div>
          </>
        </form>
        <div style={{ height: 40 }} />
      </Container>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          sx={{
            width: '100%',
            maxWidth: 300
          }}
          onClose={() => setSnackbar('')}
          variant={'filled'}
          severity={'error'}
        >
          {snackbar}
        </Alert>
      </Snackbar>
    </div>
  );

  /*Converts form values into values stored in Events API 
  Parameters:
  values: Stored Formik field values
  length: maxId in events
  */
  function convert(values: FormValues): {
    comment: string;
    endTs: Dayjs;
    buoyId: number;
    startTs: Dayjs;
    sensors: Array<any>;
  } {
    const output = {
      comment: values.comments,
      endTs: convertTime(endTime),
      buoyId: values.buoyId,
      eventTypeId: values.eventType!.id,
      startTs: convertTime(startTime),
      sensors: selectedSensors
    };
    return JSON.parse(JSON.stringify(output));
  }

  function convertTime(dateTime: Dayjs | null) {
    if (dateTime === null) {
      return '';
    }
    const dateTimeStr = dateTime.toString();
    const formattedDateTime = moment(dateTimeStr).valueOf();
    return formattedDateTime;
  }

  function handleClose() {
    props.handleCloseAddModal();
  }

  // function setSelectedSensors(selectedSensors: Array<FormSensorType>) {
  //   (formik as ReturnType<typeof useFormik>).setFieldValue('sensors', selectedSensors);
  // }
}
